/**
 * Visualization Types for Agent UI Components
 * Matches schema defined in genie/src/tools/ui_components.py
 */

export type VisualizationType =
  | 'bar_chart'
  | 'pie_chart'
  | 'line_chart'
  | 'area_chart'
  | 'table'
  | 'summary_card'
  | 'bullet_list';

export type FormatType = 'currency' | 'number' | 'percentage' | 'date';

export interface TrendInfo {
  direction: 'up' | 'down' | 'neutral';
  value?: string;
  label?: string;
}

// Base visualization interface
interface BaseVisualization {
  type: VisualizationType;
  title?: string;
}

// Bar Chart
export interface BarChartVisualization extends BaseVisualization {
  type: 'bar_chart';
  x_field: string;
  y_field: string;
  format?: FormatType;
  data: Record<string, any>[];
}

// Pie Chart
export interface PieChartVisualization extends BaseVisualization {
  type: 'pie_chart';
  label_field: string;
  value_field: string;
  data: Record<string, any>[];
}

// Line Chart
export interface LineChartVisualization extends BaseVisualization {
  type: 'line_chart';
  x_field: string;
  y_field: string;
  format?: FormatType;
  data: Record<string, any>[];
}

// Area Chart
export interface AreaChartVisualization extends BaseVisualization {
  type: 'area_chart';
  x_field: string;
  y_field: string;
  format?: FormatType;
  data: Record<string, any>[];
}

// Table
export interface TableVisualization extends BaseVisualization {
  type: 'table';
  columns: string[];
  format_columns?: Record<string, FormatType>;
  data: Record<string, any>[];

  // NEW: Editable support for bank statement import
  table_id?: string;
  editable_columns?: string[];
  select_options?: Record<string, SelectOption[]>;
  highlight_rows?: HighlightRule[];
}

export interface SelectOption {
  value: string;
  label: string;
  group?: string;  // For ledger grouping
}

export interface HighlightRule {
  column: string;
  value: string;
  color: 'red' | 'yellow' | 'green';
}

// Summary Card
export interface SummaryCardVisualization extends BaseVisualization {
  type: 'summary_card';
  title: string;
  value: string | number;
  format?: FormatType;
  trend?: TrendInfo;
}

// Bullet List
export interface BulletListVisualization extends BaseVisualization {
  type: 'bullet_list';
  items: string[];
}

// Union type for all visualizations
export type VisualizationData =
  | BarChartVisualization
  | PieChartVisualization
  | LineChartVisualization
  | AreaChartVisualization
  | TableVisualization
  | SummaryCardVisualization
  | BulletListVisualization;

// Props for visualization components
export interface VisualizationProps<T extends VisualizationData = VisualizationData> {
  data: T;
  className?: string;
}

// Formatting utilities
export const formatValue = (value: number | string, format?: FormatType): string => {
  if (typeof value === 'string') return value;

  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'percentage':
      return `${value.toFixed(1)}%`;
    case 'number':
    default:
      return value.toLocaleString('en-IN');
  }
};

export const formatDate = (value: string | Date): string => {
  const date = new Date(value);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
